import { getDriverAnnualFee } from '../../cross-site/marketplace/drivers/getDriverAnnualFee';
import { getDriverIntroApr } from '../../cross-site/marketplace/drivers/getDriverIntroApr';
import { getDriverIntroOffer } from '../../cross-site/marketplace/drivers/getDriverIntroOffer';
import { getDriverMinimumDepositDescription } from '../../cross-site/marketplace/drivers/getDriverMinimumDepositDescription';
import { getDriverRecommendedCreditScoreRange } from '../../cross-site/marketplace/drivers/getDriverRecommendedCreditScoreRange';
import { getDriverRegularApr } from '../../cross-site/marketplace/drivers/getDriverRegularApr';
import { getDriverRewardRate } from '../../cross-site/marketplace/drivers/getDriverRewardRate';
import { makeDrivers } from '../../cross-site/marketplace/drivers/utils';
import { RoundupDriverGroupEnum } from '../../cross-site/marketplace/enums';
import type { MarketplaceEntity } from '../../cross-site/marketplace/types';
import { CC_ATTRIBUTION_VERTICAL, CC_PRIMARY_CATEGORY_ID, CreditCardsPageType } from '../constants';

type RequiredFields = {
  attributionVertical: string;
  pageTemplate: string;
  pageType: string;
  primaryCategory: {
    id: string;
  };
};

function hasRequiredFields(
  creditCardsPage: Record<string, unknown>,
): creditCardsPage is RequiredFields {
  return (
    'attributionVertical' in creditCardsPage &&
    'pageTemplate' in creditCardsPage &&
    'pageType' in creditCardsPage &&
    'primaryCategory' in creditCardsPage &&
    typeof creditCardsPage.primaryCategory === 'object' &&
    creditCardsPage.primaryCategory !== null &&
    'id' in creditCardsPage.primaryCategory
  );
}

export function checkIsCreditCardsRoundupPage(creditCardsPage: Record<string, unknown>): boolean {
  if (!creditCardsPage) {
    return false;
  }
  if (!hasRequiredFields(creditCardsPage)) {
    return false;
  }

  return (
    creditCardsPage.attributionVertical.toLowerCase() === CC_ATTRIBUTION_VERTICAL.toLowerCase() &&
    creditCardsPage.primaryCategory.id === CC_PRIMARY_CATEGORY_ID &&
    creditCardsPage.pageType === CreditCardsPageType.Roundup
  );
}

const getBalanceTransferDrivers = makeDrivers([
  getDriverIntroApr,
  getDriverRegularApr,
  getDriverRewardRate,
]);

const getRewardsDrivers = makeDrivers([
  getDriverAnnualFee,
  getDriverRewardRate,
  getDriverIntroOffer,
]);

const getSubprimeDrivers = makeDrivers([
  getDriverAnnualFee,
  getDriverMinimumDepositDescription,
  getDriverRecommendedCreditScoreRange,
]);

const getClassicDrivers = makeDrivers([getDriverAnnualFee, getDriverIntroApr, getDriverRegularApr]);

export function getRoundupDrivers(marketplaceEntity: MarketplaceEntity, driverGroup: string) {
  switch (driverGroup) {
    case RoundupDriverGroupEnum.BalanceTransfer:
      return getBalanceTransferDrivers(marketplaceEntity);
    case RoundupDriverGroupEnum.Rewards:
      return getRewardsDrivers(marketplaceEntity);
    case RoundupDriverGroupEnum.Subprime:
      return getSubprimeDrivers(marketplaceEntity);
    default:
      return getClassicDrivers(marketplaceEntity);
  }
}

export enum CreditRangeEnum {
  EXCELLENT = 'EXCELLENT',
  GOOD = 'GOOD',
  FAIR = 'FAIR',
  POOR = 'POOR',
}

export const CREDIT_RANGE_LABEL_MAP = {
  [CreditRangeEnum.EXCELLENT]: 'Excellent',
  [CreditRangeEnum.GOOD]: 'Good',
  [CreditRangeEnum.FAIR]: 'Fair',
  [CreditRangeEnum.POOR]: 'Poor',
} as const satisfies Record<CreditRangeEnum, string>;

export type CreditRangeLabel = (typeof CREDIT_RANGE_LABEL_MAP)[CreditRangeEnum];

/** The order of these ranges determines the order shown in the UI for RU filtering. */
export const CREDIT_RANGES: Record<CreditRangeEnum, { min: number; max: number }> = {
  [CreditRangeEnum.EXCELLENT]: {
    min: 720,
    max: 850,
  },
  [CreditRangeEnum.GOOD]: {
    min: 690,
    max: 719,
  },
  [CreditRangeEnum.FAIR]: {
    min: 630,
    max: 689,
  },
  [CreditRangeEnum.POOR]: {
    min: 300,
    max: 629,
  },
};

// This function prepares data for the RoundupFilters component
// It returns a set of offer ids that are pre-filtered and sorted
// based on various criteria, aiding with client performance
export const getPreFilteredOfferIds = <
  T extends {
    details: {
      apr: {
        min?: number;
      };
      creditScore: {
        limitedCreditHistory: boolean;
        max: number;
        min: number;
      };
      annualFee: {
        value: number;
      };
      introOfferDetails: {
        offerString?: string;
      };
      starRating: number;
    };
    institution: {
      id: string;
      name: string;
    };
    id: string;
  },
>(
  allOffersWithTitleAndSpotlight: T[],
) => {
  const allOfferIds = new Set<string>();
  const noAnnualFeeOfferIds = new Set<string>();
  const introOfferOfferIds = new Set<string>();
  const noCreditHistoryAllowedOfferIds = new Set<string>();
  const defaultIssuersCount: Record<string, number> = {};
  const issuersIdNameMap: Record<string, string> = {};
  const offersByIssuerMap: Record<string, Set<string>> = {};
  const offersByRCSMap: Record<CreditRangeEnum, Set<string>> = Object.keys(CREDIT_RANGES).reduce(
    (prevValue: Record<string, Set<string>>, rangeName) => {
      prevValue[rangeName] = new Set<string>();
      return prevValue;
    },
    {},
  );
  const defaultCreditRangesCount: Record<CreditRangeEnum, number> = (
    Object.keys(CREDIT_RANGES) as CreditRangeEnum[]
  ).reduce(
    (acc, key) => {
      acc[key] = 0;
      return acc;
    },
    {} as Record<CreditRangeEnum, number>,
  );

  allOffersWithTitleAndSpotlight.forEach(offer => {
    const {
      details: { creditScore, annualFee, introOfferDetails },
      institution,
      id,
    } = offer;
    allOfferIds.add(id);
    if (!annualFee.value) {
      noAnnualFeeOfferIds.add(id);
    }

    if (introOfferDetails.offerString && !introOfferDetails.offerString.includes('N/A')) {
      introOfferOfferIds.add(id);
    }

    if (!issuersIdNameMap[institution.id]) {
      issuersIdNameMap[institution.id] = institution.name;
      defaultIssuersCount[institution.id] = 0;
    }

    if (!offersByIssuerMap[institution.id]) {
      offersByIssuerMap[institution.id] = new Set<string>();
    }
    offersByIssuerMap[institution.id].add(id);

    if (creditScore.limitedCreditHistory) {
      noCreditHistoryAllowedOfferIds.add(id);
    }

    Object.entries(CREDIT_RANGES).forEach(([rangeName, range]) => {
      if (range.min <= creditScore.max && range.max >= creditScore.min) {
        offersByRCSMap[rangeName as CreditRangeEnum].add(id);
      }
    });
  });

  const sortedByHighestRating = allOffersWithTitleAndSpotlight
    .toSorted((a, b) => b.details.starRating - a.details.starRating)
    .map(offer => offer.id);
  const sortedByLowestAnnualFee = allOffersWithTitleAndSpotlight
    .toSorted((a, b) => a.details.annualFee.value - b.details.annualFee.value)
    .map(offer => offer.id);
  const sortedByLowestAPR = allOffersWithTitleAndSpotlight
    .toSorted((a, b) => (a.details.apr.min ?? 0) - (b.details.apr.min ?? 0))
    .map(offer => offer.id);

  return {
    allOfferIds,
    defaultCreditRangesCount,
    defaultIssuersCount,
    introOfferOfferIds,
    issuersIdNameMap,
    noAnnualFeeOfferIds,
    noCreditHistoryAllowedOfferIds,
    offersByIssuerMap,
    offersByRCSMap,
    sortedByHighestRating,
    sortedByLowestAnnualFee,
    sortedByLowestAPR,
  };
};
